import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { DollarSign, TrendingUp, TrendingDown, Users, Search, ChevronDown, ChevronUp, Check, ChevronLeft, ChevronRight, FileText, ChevronsUpDown } from 'lucide-react';

const formatBRL = (value) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function formatMesLabel(mes) {
  const [y, m] = mes.split('-');
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${meses[parseInt(m) - 1]} ${y}`;
}

function moveMes(mes, delta) {
  const [y, m] = mes.split('-').map(Number);
  const date = new Date(y, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

const StatusBadge = ({ status }) => {
  const map = {
    pago: 'bg-green-100 text-green-700',
    parcial: 'bg-amber-100 text-amber-700',
    pendente: 'bg-red-100 text-red-600',
  };
  const label = { pago: 'Pago', parcial: 'Parcial', pendente: 'Pendente' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {label[status] || status}
    </span>
  );
};

const TipoBadge = ({ tipo }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    tipo === 'pacote' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
  }`}>
    {tipo === 'pacote' ? 'Pacote' : 'Por Sessão'}
  </span>
);

const FILTROS = [
  { key: 'todos', label: 'Todos' },
  { key: 'sessao', label: 'Por Sessão' },
  { key: 'pacote', label: 'Pacote' },
  { key: 'pago', label: 'Pagos' },
  { key: 'parcial', label: 'Parciais' },
  { key: 'pendente', label: 'Pendentes' },
];

export default function FinanceiroPage() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [busca, setBusca] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [novoValor, setNovoValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [mes, setMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [sortCol, setSortCol] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 text-gray-300 ml-1 inline" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-blue-500 ml-1 inline" />
      : <ChevronDown className="w-3 h-3 text-blue-500 ml-1 inline" />;
  };

  const Th = ({ children, col, className = '' }) => (
    <th
      className={`text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 cursor-pointer select-none hover:text-gray-700 ${className}`}
      onClick={() => handleSort(col)}
    >
      {children}<SortIcon col={col} />
    </th>
  );

  const fetchDados = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/financeiro?mes=${mes}`);
      setDados(data);
    } catch {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => { fetchDados(); }, [fetchDados]);

  const handleSalvarRecebido = async (patientId) => {
    const valor = parseFloat(novoValor);
    if (isNaN(valor) || valor < 0) return toast.error('Valor inválido');
    setSalvando(true);
    try {
      const resp = await api.patch(`/financeiro/${patientId}/recebido`, { valorRecebido: valor, mes });
      const updated = resp.data?.data;
      toast.success('Valor atualizado!');
      setEditandoId(null);

      if (updated) {
        setDados(prev => {
          const newData = (prev.data || []).map(p =>
            p._id === patientId
              ? {
                  ...p,
                  valorRecebido: updated.valorRecebido,
                  receitaPrevista: updated.receitaPrevista,
                  receitaRealizada: updated.receitaRealizada,
                  saldoPendente: updated.saldoPendente,
                  statusFinanceiro: updated.statusFinanceiro,
                  sessoesRealizadas: updated.sessoesRealizadas,
                  sessoesAgendadas: updated.sessoesAgendadas,
                }
              : p
          );
          // Recalcula totais
          const totais = newData.reduce((acc, p) => ({
            receitaPrevista: acc.receitaPrevista + (p.receitaPrevista || 0),
            receitaRealizada: acc.receitaRealizada + (p.receitaRealizada || 0),
            saldoPendente: acc.saldoPendente + (p.saldoPendente || 0),
          }), { receitaPrevista: 0, receitaRealizada: 0, saldoPendente: 0 });
          return { ...prev, data: newData, totais };
        });
      } else {
        fetchDados();
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao salvar';
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  };

  const handleGerarRecibo = async (patientId, nome) => {
    try {
      const resp = await api.get(`/financeiro/${patientId}/recibo?mes=${mes}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo-${nome.replace(/\s+/g, '-')}-${mes}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Não foi possível gerar o recibo. Verifique se há pagamento confirmado neste mês.');
    }
  };

  const pacientesFiltrados = (dados?.data || []).filter(p => {
    const matchBusca = busca.trim() === '' ||
      p.nome.toLowerCase().includes(busca.toLowerCase()) ||
      (p.diagnosisTitle || '').toLowerCase().includes(busca.toLowerCase());
    const matchFiltro = filtro === 'todos' ? true
      : filtro === 'sessao' ? p.tipoCobranca === 'sessao'
      : filtro === 'pacote' ? p.tipoCobranca === 'pacote'
      : p.statusFinanceiro === filtro;
    return matchBusca && matchFiltro;
  }).sort((a, b) => {
    let va, vb;
    if (sortCol === 'nome') { return sortDir === 'asc' ? a.nome.localeCompare(b.nome, 'pt-BR') : b.nome.localeCompare(a.nome, 'pt-BR'); }
    if (sortCol === 'previsto') { va = a.receitaPrevista || 0; vb = b.receitaPrevista || 0; }
    if (sortCol === 'recebido') { va = a.receitaRealizada || 0; vb = b.receitaRealizada || 0; }
    if (sortCol === 'pendente') { va = a.saldoPendente || 0; vb = b.saldoPendente || 0; }
    if (sortCol === 'sessoes') { va = a.sessoesRealizadas || 0; vb = b.sessoesRealizadas || 0; }
    if (sortCol === 'status') { const order = { pago: 0, parcial: 1, pendente: 2 }; va = order[a.statusFinanceiro] ?? 3; vb = order[b.statusFinanceiro] ?? 3; }
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const { totais = {}, totalPacientes = 0 } = dados || {};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Financeiro</h1>
          <p className="text-gray-500 mt-1">Controle financeiro baseado nas sessões cadastradas</p>
        </div>
        {/* Navegador de mês */}
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
          <button onClick={() => setMes(m => moveMes(m, -1))} className="text-gray-500 hover:text-gray-700 p-0.5">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-gray-700 w-24 text-center">{formatMesLabel(mes)}</span>
          <button onClick={() => setMes(m => moveMes(m, 1))} className="text-gray-500 hover:text-gray-700 p-0.5">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: <TrendingUp className="w-6 h-6 text-blue-600" />, color: 'bg-blue-50', label: 'Receita Prevista', value: formatBRL(totais.receitaPrevista) },
          { icon: <DollarSign className="w-6 h-6 text-green-600" />, color: 'bg-green-50', label: 'Receita Realizada', value: formatBRL(totais.receitaRealizada) },
          { icon: <TrendingDown className="w-6 h-6 text-red-500" />, color: 'bg-red-50', label: 'Saldo Pendente', value: formatBRL(totais.saldoPendente) },
          { icon: <Users className="w-6 h-6 text-purple-600" />, color: 'bg-purple-50', label: 'Total de Pacientes', value: totalPacientes },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${card.color}`}>
              {card.icon}
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Busca */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Buscar paciente por nome ou diagnóstico..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTROS.map(f => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filtro === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {pacientesFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <DollarSign className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">Nenhum paciente encontrado para este filtro.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <Th col="nome">Paciente</Th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Tipo</th>
                  <Th col="sessoes">Sessões</Th>
                  <Th col="previsto">Previsto</Th>
                  <Th col="recebido">Recebido</Th>
                  <Th col="pendente">Pendente</Th>
                  <Th col="status">Status</Th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pacientesFiltrados.map(p => (
                  <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <p className="text-sm font-medium text-gray-900">{p.nome}</p>
                      <p className="text-xs text-gray-400">{p.diagnosisTitle}</p>
                    </td>
                    <td className="px-5 py-4"><TipoBadge tipo={p.tipoCobranca} /></td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      <span className="font-medium">{p.sessoesRealizadas}</span>
                      <span className="text-gray-400"> / {p.sessoesAgendadas || 0} agendadas</span>
                      <p className="text-xs text-gray-400">{p.numberOfSessions} total</p>
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-800">{formatBRL(p.receitaPrevista)}</td>
                    <td className="px-5 py-4">
                      {editandoId === p._id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={novoValor}
                            min={0}
                            step="0.01"
                            onChange={e => setNovoValor(e.target.value)}
                            className="w-28 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSalvarRecebido(p._id)}
                            disabled={salvando}
                            className="w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditandoId(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                      ) : p.tipoCobranca === 'pacote' ? (
                        <button
                          onClick={() => { setEditandoId(p._id); setNovoValor(String(p.valorRecebido || 0)); }}
                          className="text-sm font-medium text-gray-800 hover:text-blue-600 transition-colors flex items-center gap-1 group"
                          title="Clique para editar valor recebido"
                        >
                          {formatBRL(p.receitaRealizada)}
                          <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-blue-400" />
                        </button>
                      ) : (
                        <span className="text-sm font-medium text-gray-800">{formatBRL(p.receitaRealizada)}</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-red-600">{formatBRL(p.saldoPendente)}</td>
                    <td className="px-5 py-4"><StatusBadge status={p.statusFinanceiro} /></td>
                    <td className="px-5 py-4">
                      {p.statusFinanceiro === 'pago' && (
                        <button
                          onClick={() => handleGerarRecibo(p._id, p.nome)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          title="Gerar recibo em PDF"
                        >
                          <FileText className="w-3.5 h-3.5" /> Recibo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}